import { AVAILABILITY_RESULTS, type AvailabilityResult } from './bookingFlowAnalyticsTypes.ts';

export const BOOKING_FLOW_AVAILABILITY_SCHEMA_VERSION = 1 as const;
// Structural transport ceiling; the unit's online maximum is a business rule.
export const BOOKING_FLOW_AVAILABILITY_MAX_HEADCOUNT = 10000;
export const BOOKING_FLOW_AVAILABILITY_MAX_DURATION_MINUTES = 24 * 60;
export const BOOKING_FLOW_AVAILABILITY_DEFAULT_TIMEZONE = 'Europe/Budapest';
export const BOOKING_FLOW_AVAILABILITY_DEFAULT_DURATION_MINUTES = 120;

export interface BookingFlowAvailabilityRequest {
  unitId: string;
  bookingFlowId: string;
  requestedDateKey: string;
  startTimeBucket: string;
  headcount: number;
  startTime: string;
  endTime: string;
  selectionRevision: number;
}

export interface BookingFlowAvailabilityResponse {
  schemaVersion: typeof BOOKING_FLOW_AVAILABILITY_SCHEMA_VERSION;
  unitId: string;
  bookingFlowId: string;
  availabilityDecisionId: string;
  requestedDateKey: string;
  startTimeBucket: string;
  headcount: number;
  selectionRevision: number;
  availabilityResult: AvailabilityResult;
  observedAt: string;
}

const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9:_-]{0,127}$/;
const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
const TIME_BUCKET = /^([01]\d|2[0-3]):[0-5]\d$/;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;
const REQUEST_KEYS = new Set([
  'unitId',
  'bookingFlowId',
  'requestedDateKey',
  'startTimeBucket',
  'headcount',
  'startTime',
  'endTime',
  'selectionRevision',
]);
const RESPONSE_KEYS = new Set([
  'schemaVersion',
  'unitId',
  'bookingFlowId',
  'availabilityDecisionId',
  'requestedDateKey',
  'startTimeBucket',
  'headcount',
  'selectionRevision',
  'availabilityResult',
  'observedAt',
]);

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value)
  && typeof value === 'object'
  && !Array.isArray(value);
const hasExactKeys = (value: Record<string, unknown>, keys: Set<string>) => (
  Object.keys(value).length === keys.size && Object.keys(value).every(key => keys.has(key))
);
const isIdentifier = (value: unknown): value is string => typeof value === 'string' && IDENTIFIER.test(value);
const isCalendarDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !DATE_KEY.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};
const isTimeBucket = (value: unknown): value is string => typeof value === 'string' && TIME_BUCKET.test(value);
const isHeadcount = (value: unknown): value is number => Number.isInteger(value) && Number(value) >= 1 && Number(value) <= 99;
const isPositiveRevision = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) > 0;
const isIsoTimestamp = (value: unknown): value is string => typeof value === 'string'
  && ISO_TIMESTAMP.test(value)
  && !Number.isNaN(Date.parse(value));

const formatterCache = new Map<string, Intl.DateTimeFormat>();
const getTimeZoneFormatter = (timeZone: string) => {
  const cached = formatterCache.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  // Force invalid timezone failure before caching.
  formatter.format(new Date(0));
  formatterCache.set(timeZone, formatter);
  return formatter;
};

const timeZoneParts = (instantMs: number, timeZone: string) => {
  const values = Object.fromEntries(
    getTimeZoneFormatter(timeZone)
      .formatToParts(new Date(instantMs))
      .map(part => [part.type, part.value]),
  );
  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
};

const calendarDateOffset = (dateKey: string, days: number) => {
  const value = new Date(`${dateKey}T00:00:00.000Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
};

/** Resolves a unit-local wall-clock minute to the earliest matching instant.
 * Non-existent DST gap minutes fail closed instead of being shifted silently.
 */
export const resolveUnitLocalDateTimeIso = (input: {
  requestedDateKey: string;
  timeBucket: string;
  timeZone: string;
}): string | null => {
  if (!isCalendarDate(input.requestedDateKey) || !isTimeBucket(input.timeBucket)) return null;
  const [year, month, day] = input.requestedDateKey.split('-').map(Number);
  const [hour, minute] = input.timeBucket.split(':').map(Number);
  const wallClockMs = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  try {
    const candidates = new Set<number>();
    for (const sampleHours of [-36, -24, -12, 0, 12, 24, 36]) {
      const sampleMs = wallClockMs + sampleHours * 60 * 60 * 1000;
      const local = timeZoneParts(sampleMs, input.timeZone);
      const localAsUtcMs = Date.UTC(
        local.year,
        local.month - 1,
        local.day,
        local.hour,
        local.minute,
        local.second,
      );
      const offsetMs = localAsUtcMs - sampleMs;
      const candidateMs = wallClockMs - offsetMs;
      const candidate = timeZoneParts(candidateMs, input.timeZone);
      if (
        candidate.year === year
        && candidate.month === month
        && candidate.day === day
        && candidate.hour === hour
        && candidate.minute === minute
        && candidate.second === 0
      ) candidates.add(candidateMs);
    }
    const earliest = [...candidates].sort((left, right) => left - right)[0];
    return earliest === undefined ? null : new Date(earliest).toISOString();
  } catch {
    return null;
  }
};

export const resolveBookingFlowAvailabilityInterval = (input: {
  requestedDateKey: string;
  startTimeBucket: string;
  endTimeBucket?: string | null;
  timeZone?: string | null;
  defaultDurationMinutes?: number;
}): { startTime: string; endTime: string } | null => {
  const timeZone = input.timeZone || BOOKING_FLOW_AVAILABILITY_DEFAULT_TIMEZONE;
  const startTime = resolveUnitLocalDateTimeIso({
    requestedDateKey: input.requestedDateKey,
    timeBucket: input.startTimeBucket,
    timeZone,
  });
  if (!startTime) return null;

  if (!input.endTimeBucket) {
    const duration = input.defaultDurationMinutes ?? BOOKING_FLOW_AVAILABILITY_DEFAULT_DURATION_MINUTES;
    if (!Number.isSafeInteger(duration) || duration < 1 || duration > 24 * 60) return null;
    return {
      startTime,
      endTime: new Date(Date.parse(startTime) + duration * 60 * 1000).toISOString(),
    };
  }
  if (!isTimeBucket(input.endTimeBucket)) return null;
  const endDateKey = input.endTimeBucket <= input.startTimeBucket
    ? calendarDateOffset(input.requestedDateKey, 1)
    : input.requestedDateKey;
  const endTime = resolveUnitLocalDateTimeIso({
    requestedDateKey: endDateKey,
    timeBucket: input.endTimeBucket,
    timeZone,
  });
  if (!endTime || Date.parse(endTime) <= Date.parse(startTime)) return null;
  return { startTime, endTime };
};

export const isBookingFlowAvailabilityRequest = (value: unknown): value is BookingFlowAvailabilityRequest => {
  if (!isRecord(value) || !hasExactKeys(value, REQUEST_KEYS)) return false;
  if (!isIdentifier(value.unitId) || !isIdentifier(value.bookingFlowId) || !isCalendarDate(value.requestedDateKey)) return false;
  if (!isTimeBucket(value.startTimeBucket) || !isHeadcount(value.headcount)) return false;
  if (value.headcount > BOOKING_FLOW_AVAILABILITY_MAX_HEADCOUNT) return false;
  if (!isIsoTimestamp(value.startTime) || !isIsoTimestamp(value.endTime)) return false;
  const durationMs = Date.parse(value.endTime as string) - Date.parse(value.startTime as string);
  if (
    durationMs <= 0
    || durationMs > BOOKING_FLOW_AVAILABILITY_MAX_DURATION_MINUTES * 60 * 1000
    || durationMs % 60_000 !== 0
  ) return false;
  return isPositiveRevision(value.selectionRevision);
};

export const isBookingFlowAvailabilityResponse = (value: unknown): value is BookingFlowAvailabilityResponse => {
  if (!isRecord(value) || !hasExactKeys(value, RESPONSE_KEYS)) return false;
  if (value.schemaVersion !== BOOKING_FLOW_AVAILABILITY_SCHEMA_VERSION) return false;
  if (
    !isIdentifier(value.unitId)
    || !isIdentifier(value.bookingFlowId)
    || !isIdentifier(value.availabilityDecisionId)
    || !isCalendarDate(value.requestedDateKey)
  ) return false;
  if (!isTimeBucket(value.startTimeBucket) || !isHeadcount(value.headcount)) return false;
  if (value.headcount > BOOKING_FLOW_AVAILABILITY_MAX_HEADCOUNT || !isIsoTimestamp(value.observedAt)) return false;
  if (!isPositiveRevision(value.selectionRevision)) return false;
  return typeof value.availabilityResult === 'string'
    && AVAILABILITY_RESULTS.includes(value.availabilityResult as AvailabilityResult);
};

export const responseMatchesAvailabilityRequest = (
  response: BookingFlowAvailabilityResponse,
  request: BookingFlowAvailabilityRequest,
): boolean => response.unitId === request.unitId
  && response.bookingFlowId === request.bookingFlowId
  && response.requestedDateKey === request.requestedDateKey
  && response.startTimeBucket === request.startTimeBucket
  && response.headcount === request.headcount
  && response.selectionRevision === request.selectionRevision;
